import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Check, AlertCircle, User, CreditCard, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/professional/login');
          return;
        }

        // Fetch user info
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userResponse.data);

        // Fetch subscription plans
        const plansResponse = await axios.get(`${API}/subscriptions/plans`);
        setPlans(plansResponse.data.plans);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/professional/login');
        } else {
          setError('Erreur lors du chargement des données');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSubscribe = async (planId) => {
    setError('');
    setProcessingPayment(true);

    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API}/payments/checkout`,
        { 
          plan_id: planId,
          origin_url: originUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la session de paiement');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 pt-32 pb-20">
        {/* User Info Section */}
        <div className="mb-12">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 bg-red-600 rounded-full flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl text-white">{user?.garage_name}</CardTitle>
                    <CardDescription className="text-gray-400">{user?.email}</CardDescription>
                  </div>
                </div>
                <Badge 
                  className={
                    user?.subscription_status === 'active' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-600 text-white'
                  }
                >
                  {user?.subscription_status === 'active' ? '✓ Abonnement actif' : 'Aucun abonnement'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 text-gray-300">
                <div>
                  <p className="text-sm text-gray-400">Adresse</p>
                  <p className="font-semibold">{user?.address}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Téléphone</p>
                  <p className="font-semibold">{user?.phone}</p>
                </div>
                {user?.subscription_plan && (
                  <div>
                    <p className="text-sm text-gray-400">Plan actuel</p>
                    <p className="font-semibold capitalize">{user.subscription_plan}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Subscription Plans */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Nos Prestations</h2>
            <p className="text-gray-400 text-lg">
              Solutions anti-pollution et diagnostic - Paiement unique
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`bg-slate-800/50 border-slate-700 ${
                  user?.subscription_plan === plan.id ? 'ring-2 ring-green-600' : ''
                }`}
              >
                <CardHeader>
                  {plan.id === 'fap_egr' && (
                    <Badge className="bg-blue-600 text-white w-fit mb-2">Le plus populaire</Badge>
                  )}
                  <CardTitle className="text-white text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-gray-400">
                    Prestation unique
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-white">{plan.price.toFixed(0)}€</span>
                    <span className="text-gray-400 ml-2">TTC</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <Check className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {user?.subscription_plan === plan.id && user?.subscription_status === 'service_paid' ? (
                    <Button 
                      disabled 
                      className="w-full bg-green-600 text-white"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Prestation payée
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPayment}
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                      data-testid={`subscribe-${plan.id}-button`}
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {processingPayment ? 'Traitement...' : 'Commander'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <Card className="bg-blue-900/20 border-blue-700">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-4">
              <AlertCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-gray-300">
                <p className="font-semibold text-white mb-2">Paiement sécurisé avec Stripe</p>
                <p className="text-sm">
                  Tous les paiements sont sécurisés et cryptés. Vous pouvez résilier votre abonnement à tout moment.
                  Le montant affiché est le prix EXACT que vous paierez - plus de surprises!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default ProfessionalDashboard;
